import type { PlatformContract } from "@ondit/publishing";
import type { Block } from "~/core/api/index";

/** A page is its blocks; the media ones are the pictures, clips and narration. */
export type Piece = Block;
export type MediaPiece = Extract<Block, { type: "image" | "video" | "audio" }>;
export type MediaKind = MediaPiece["type"];

export interface Work {
	title: string;
	summary: string;
	pieces: Piece[];
}

export const MEDIA_KINDS: MediaKind[] = ["image", "video", "audio"];

export function isMedia(piece: Piece): piece is MediaPiece {
	return (MEDIA_KINDS as string[]).includes(piece.type);
}

/** The characters a platform counts: the words, not the markup around them. */
export function textLength(pieces: Piece[]): number {
	let n = 0;
	for (const p of pieces) {
		if (p.type === "answer") n += p.text.length;
		else if (p.type === "section") n += p.heading.length + p.md.replace(/[#*_`>[\]()-]/g, "").length;
		else if (p.type === "faq") for (const i of p.items) n += i.q.length + i.a.length;
		else if (p.type === "stat") n += p.label.length + p.value.length;
	}
	return n;
}

export type Tone = "ok" | "fail" | "note";
export interface Check {
	tone: Tone;
	text: [string, string];
}

const FORMAT: Record<string, [string, string]> = {
	rich_text: ["rich text", "富文本"],
	markdown: ["Markdown", "Markdown"],
	plain: ["plain text", "纯文本"],
};

/**
 * What a platform will make of this work, from its contract.
 *
 * Only media with a file counts: a placeholder somebody has not generated yet
 * is exactly the thing the platform will refuse.
 */
// ponytail: counts and kinds only; image size, aspect and clip length need the
// file's metadata, which `MediaLimits` already describes for when it is loaded.
export function fitTo(work: Work, contract: PlatformContract): Check[] {
	const out: Check[] = [];
	const fields = contract.preview.fields;
	const title = fields.find((f) => f.key === "title");
	if (title) {
		const n = work.title.trim().length;
		if (!n) out.push({ tone: "fail", text: ["Needs a title", "需要标题"] });
		else if (title.max_length && n > title.max_length)
			out.push({
				tone: "fail",
				text: [`Title ${n}/${title.max_length} characters`, `标题 ${n}/${title.max_length} 字`],
			});
		else out.push({ tone: "ok", text: ["Title fits", "标题符合"] });
	}

	const body = fields.find((f) => f.key === "body");
	if (body) {
		const n = textLength(work.pieces);
		if (body.required && !n) out.push({ tone: "fail", text: ["Needs body text", "需要正文"] });
		if (body.max_length && n > body.max_length)
			out.push({
				tone: "fail",
				text: [`Body ${n}/${body.max_length} characters`, `正文 ${n}/${body.max_length} 字`],
			});
		const format = FORMAT[body.format ?? "plain"];
		out.push({ tone: "note", text: [`Body goes out as ${format[0]}`, `正文以${format[1]}发出`] });
		if (body.format !== "rich_text" && body.format !== "markdown" && work.pieces.some((p) => p.type === "table"))
			out.push({ tone: "note", text: ["Tables are flattened to text", "表格会被压成文字"] });
	}

	const filled = work.pieces.filter((p): p is MediaPiece => isMedia(p) && !!p.file_id);
	const images = filled.filter((p) => p.type === "image").length;
	const videos = filled.filter((p) => p.type === "video").length;
	const media = contract.preview.media;
	if (!media) {
		if (images + videos) out.push({ tone: "note", text: ["Takes no pictures or video", "不收图片和视频，只发文字"] });
	} else {
		const takesImage = media.accept.includes("image/");
		const takesVideo = media.accept.includes("video/");
		const usable = (takesImage ? images : 0) + (takesVideo ? videos : 0);
		const before = out.length;
		if (media.required && !usable)
			out.push({
				tone: "fail",
				text: takesImage
					? takesVideo
						? ["Needs a picture or a video", "需要至少一张图片或一个视频"]
						: ["Needs at least one picture", "需要至少一张图片"]
					: ["Needs a video", "需要一个视频"],
			});
		if (videos && !takesVideo) out.push({ tone: "fail", text: ["Takes no video", "不接受视频"] });
		if (images && !takesImage) out.push({ tone: "fail", text: ["Takes no pictures", "不接受图片"] });
		if (media.max_count && usable > media.max_count)
			out.push({
				tone: "fail",
				text: [`At most ${media.max_count} media, has ${usable}`, `最多 ${media.max_count} 个素材，现有 ${usable} 个`],
			});
		if (media.video_alone && videos && images)
			out.push({ tone: "fail", text: ["A video has to go alone", "视频须单独发布，不能配图"] });
		if (usable && out.length === before) out.push({ tone: "ok", text: ["Media fits", "素材符合"] });
	}
	if (filled.some((p) => p.type === "audio"))
		out.push({ tone: "note", text: ["Audio is not posted; use it as narration", "音频不会直接发出，可作视频配音"] });
	return out;
}

/**
 * A starting structure for the platforms chosen: the page's answer-first
 * shape, plus the media the strictest of them will refuse to go out without.
 */
export function outline(contracts: PlatformContract[]): Piece[] {
	const media = contracts.map((c) => c.preview.media).filter((m) => m?.required);
	const needsVideo = media.some((m) => m && !m.accept.includes("image/"));
	const needsImage = media.some((m) => m?.accept.includes("image/"));
	const wantsLong = contracts.some((c) => c.preview.layout === "article" || c.preview.layout === "newsletter");
	const pieces: Piece[] = [];
	if (needsImage || wantsLong) pieces.push({ type: "image", file_id: "", alt: "", caption: null });
	pieces.push({ type: "answer", text: "" });
	if (needsVideo) pieces.push({ type: "video", file_id: "", caption: null, transcript: null });
	pieces.push({ type: "section", heading: "", md: "" });
	if (wantsLong) pieces.push({ type: "section", heading: "", md: "" }, { type: "faq", items: [{ q: "", a: "" }] });
	pieces.push({ type: "sources", items: [{ label: "", url: "" }] });
	return pieces;
}
